import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatusColor = (status) => {
  switch (status) {
    case "pending": case "waiting": return "bg-terracotta";
    case "confirmed": case "contacted": case "in_progress": return "bg-olive";
    case "completed": case "enrolled": return "bg-charcoal";
    case "cancelled": case "removed": return "bg-red-200 text-red-800";
    default: return "bg-sand text-charcoal";
  }
};

export const WaitlistTab = ({
  waitlist,
  getAuthHeaders,
  fetchAllData
}) => {
  const handleStatusUpdate = async (type, id, newStatus) => {
    try {
      const headers = getAuthHeaders();
      await axios.patch(`${API}/${type}/${id}`, { status: newStatus }, { headers });
      toast.success("Status updated");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteWaitlist = async (id, puppyName) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/waitlist/${id}`, { headers });
      toast.success(`${puppyName} removed from waitlist`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove from waitlist");
    }
  };

  return (
    <div className="card-premium p-0 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-sand/50">
            <TableHead>Position</TableHead>
            <TableHead>Puppy</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Program</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {waitlist.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-stone">
                No waitlist entries
              </TableCell>
            </TableRow>
          ) : (
            waitlist.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>#{entry.position}</TableCell>
                <TableCell className="font-medium">{entry.puppy_name}</TableCell>
                <TableCell>{entry.owner_name}</TableCell>
                <TableCell>{entry.program_name}</TableCell>
                <TableCell className="text-xs">
                  <div>{entry.contact_email}</div>
                  <div>{entry.contact_phone}</div>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(entry.status)}>{entry.status}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Select
                      value={entry.status}
                      onValueChange={(v) => handleStatusUpdate("waitlist", entry.id, v)}
                    >
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="waiting">Waiting</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="enrolled">Enrolled</SelectItem>
                        <SelectItem value="removed">Removed</SelectItem>
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove from Waitlist</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove <strong>{entry.puppy_name}</strong> from the waitlist?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => handleDeleteWaitlist(entry.id, entry.puppy_name)}
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
